import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, MenuItem, Select, Tooltip, Typography } from "@mui/material";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import CampaignRoundedIcon from "@mui/icons-material/CampaignRounded";
import ViewTimelineRoundedIcon from "@mui/icons-material/ViewTimelineRounded";
import LayersRoundedIcon from "@mui/icons-material/LayersRounded";
import TaskAltRoundedIcon from "@mui/icons-material/TaskAltRounded";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import DirectionsRunRoundedIcon from "@mui/icons-material/DirectionsRunRounded";
import AssignmentRoundedIcon from "@mui/icons-material/AssignmentRounded";
import DonutSmallRoundedIcon from "@mui/icons-material/DonutSmallRounded";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import UnfoldLessRoundedIcon from "@mui/icons-material/UnfoldLessRounded";
import UnfoldMoreRoundedIcon from "@mui/icons-material/UnfoldMoreRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import SearchOffRoundedIcon from "@mui/icons-material/SearchOffRounded";
import moment from "moment";
import axios from "axios";
import * as XLSX from "xlsx";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import BreadcrumbsComponent from "../BreadcrumbsComponent";
import SearchTextField from "../SearchTextField";
import AllIssuesTable from "./AllIssuesTable";
import GroupAvatars from "../AvatarGroupLogos";
import AddPeopleToProject from "../../containers/Dashboard/ProjectPages/AddPeopleToProject";
import TaskWorkFlowModalPage from "../Modal/TaskWorkFlow";
import CreateBacklogIssue from "../Create_Backlogissue";
import { NormalSkeleton, CircularSkeleton } from "../../common/Skeleton";
import { setSkeletonStatus } from "../../common/Redux/Reducer";
import API_BASE_URL from "../config/appConfig";
import { useToast } from "../Toast";
import { usePageSearch } from "../../context/PageSearchContext";
import {
  ALL_SPRINTS,
  BACKLOG,
  STATUS_META,
  buildSprintGroups,
  exportRowOf,
  fileNameFor,
  matchesSearch,
  sheetNamesFor,
  sprintColorOf,
} from "./sprintGroups";
import "./issueslist.scss";

const percent = (part, whole) => (whole ? Math.round((part * 100) / whole) : 0);
const plural = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;

const dateRangeOf = (group) => {
  if (!group?.startDate && !group?.endDate) return "";
  const start = group.startDate ? moment(group.startDate).format("DD MMM") : "—";
  const end = group.endDate ? moment(group.endDate).format("DD MMM YYYY") : "—";
  return `${start} – ${end}`;
};

/* "4 days left", "Ends today", "Ended 3 days ago" — only for a running sprint */
const timeLeftOf = (group) => {
  if (group?.status !== "active" || !group.endDate) return "";
  const days = moment(group.endDate).endOf("day").diff(moment(), "days");
  if (days > 0) return `${plural(days, "day")} left`;
  if (days === 0) return "Ends today";
  return `Ended ${plural(-days, "day")} ago`;
};

const StatusPill = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.planned;
  return (
    <span className={`as-status ${status === "active" ? "is-live" : ""}`}>
      <span className="as-status__dot" />
      {meta.label}
    </span>
  );
};

const ProgressBar = ({ done, total, label }) => {
  const value = percent(done, total);
  return (
    <span className="as-bar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={value} aria-label={label}>
      <span style={{ width: `${value}%` }} />
    </span>
  );
};

const Stat = ({ icon, value, label, detail, delay }) => (
  <div className="as-stat" style={{ "--d": `${delay}ms` }}>
    <span className="as-stat__icon">{icon}</span>
    <span className="as-stat__text">
      <strong>{value}</strong>
      <span>{label}</span>
      {detail ? <em title={detail}>{detail}</em> : null}
    </span>
  </div>
);

/* a sprint's tasks under a header that says where the sprint stands */
const SprintSection = ({ group, collapsible, collapsed, onToggle, onFocus, onOpenIssue, searching, pageSize, index }) => {
  const timeLeft = timeLeftOf(group);
  const range = dateRangeOf(group);
  const bodyId = `as-section-${group.id}`;
  const tint = sprintColorOf(group);

  return (
    <section
      className={`as-section is-${group.status} ${collapsed ? "is-collapsed" : ""}`}
      style={{ "--d": `${Math.min(index, 8) * 45}ms`, "--sprint-color": tint.color, "--sprint-soft": tint.soft }}
      aria-label={group.name}
    >
      <header className="as-section__head">
        {collapsible ? (
          <button
            type="button"
            className="as-section__toggle"
            onClick={onToggle}
            aria-expanded={!collapsed}
            aria-controls={bodyId}
            aria-label={`${collapsed ? "Show" : "Hide"} tasks in ${group.name}`}
          >
            <ExpandMoreRoundedIcon />
          </button>
        ) : null}

        <div className="as-section__identity">
          <span className="as-section__icon" aria-hidden="true">
            {group.id === BACKLOG ? <LayersRoundedIcon /> : <DirectionsRunRoundedIcon />}
          </span>
          <div className="as-section__titles">
            <span className="as-section__name-row">
              <h2 className="as-section__name">{group.name}</h2>
              <StatusPill status={group.status} />
            </span>
            <span className="as-section__meta">
              {range ? (
                <span>
                  <EventRoundedIcon />
                  {range}
                </span>
              ) : null}
              {timeLeft ? <span className="as-section__left">{timeLeft}</span> : null}
              {group.id === BACKLOG ? <span>Not planned into a sprint</span> : null}
            </span>
          </div>
        </div>

        <div className="as-section__summary">
          <span className="as-section__progress">
            <span className="as-section__progress-label">
              <TaskAltRoundedIcon />
              {group.done}/{group.total} done
            </span>
            <ProgressBar done={group.done} total={group.total} label={`${group.name} progress`} />
          </span>
          <span className="as-chip" title="Story points done / planned">
            <DonutSmallRoundedIcon />
            {group.donePoints}/{group.points} pts
          </span>
          <span className="as-count" aria-label={plural(group.total, "task")}>
            {group.total}
          </span>
          {onFocus ? (
            <Tooltip title={`Show only ${group.name}`}>
              <button type="button" className="as-section__focus" onClick={onFocus} aria-label={`Open ${group.name}`}>
                <ArrowForwardRoundedIcon />
              </button>
            </Tooltip>
          ) : null}
        </div>
      </header>

      {!collapsed ? (
        <div className="as-section__body" id={bodyId}>
          <AllIssuesTable
            issues={group.issues}
            onOpenIssue={onOpenIssue}
            showSprint={false}
            pageSize={pageSize}
            emptyTitle={searching ? "No matching tasks" : "No tasks in this sprint yet"}
            emptyHint={searching ? "Nothing in this sprint matches your search." : "Plan tasks into it from the backlog."}
          />
        </div>
      ) : null}
    </section>
  );
};

const IssuesList = () => {
  /* shared with the top bar, so either box filters this page */
  const [searchTerm, setSearchTerm] = usePageSearch("Search tasks…");
  const [searchParams, setSearchParams] = useSearchParams();
  const [openAddPeopleModel, setOpenAddPeopleModel] = useState(false);
  const [issues, setIssues] = useState([]);
  const [sprints, setSprints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [collapsedIds, setCollapsedIds] = useState(null);
  const [openIssueId, setOpenIssueId] = useState(null);
  const [creating, setCreating] = useState(false);

  const toast = useToast();
  const dispatch = useDispatch();
  const ProjectName = localStorage.getItem("projectName");
  const { Projects, Skeleton } = useSelector((state) => state.ProjamAppSlice);
  const backlogId = Projects?.selectedProject?.selectedProjectBackLogId;
  const activeSprint = Projects?.selectedProject?.ActiveSprint;
  const projectTitle = Projects?.selectedProject?.selectedProjectData?.title || ProjectName || "";

  // the chosen sprint lives in the URL, so a copied link opens the same view
  const selectedId = searchParams.get("sprint") || ALL_SPRINTS;
  const selectSprint = (id) => {
    const next = new URLSearchParams(searchParams);
    if (!id || id === ALL_SPRINTS) next.delete("sprint");
    else next.set("sprint", id);
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    if (!backlogId) return;
    axios(`${API_BASE_URL}/sprints?backlogId=${backlogId}`)
      .then((response) => setSprints(Array.isArray(response?.data) ? response.data : []))
      .catch((error) => console.error("Could not load sprints:", error));
  }, [backlogId]);

  /* Re-fetched when the issue dialog or the create dialog closes, since
     either may have changed what is in a sprint. */
  const loadIssues = useCallback(async () => {
    if (!backlogId) {
      /* nothing to fetch, so stop waiting rather than sit under a skeleton */
      setLoading(false);
      dispatch(setSkeletonStatus({ Skeleton: false }));
      return;
    }
    try {
      const response = await axios.get(`${API_BASE_URL}/issues?backlogId=${backlogId}`);
      setIssues(Array.isArray(response?.data) ? response.data : []);
    } catch (error) {
      console.error("Error loading issues:", error);
      toast.error("Could not load the tasks");
    } finally {
      setLoading(false);
      dispatch(setSkeletonStatus({ Skeleton: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backlogId, dispatch]);

  useEffect(() => {
    if (openIssueId || creating) return;
    loadIssues();
  }, [loadIssues, openIssueId, creating]);

  const groups = useMemo(() => buildSprintGroups(issues, sprints), [issues, sprints]);

  /* completed sprints start folded, apart from the latest one, so the page
     opens on what is current without hiding the most recent history */
  useEffect(() => {
    if (collapsedIds !== null || loading || !groups.length) return;
    const completed = groups.filter((group) => group.status === "completed");
    setCollapsedIds(new Set(completed.slice(1).map((group) => group.id)));
  }, [groups, loading, collapsedIds]);

  const selectedGroup = selectedId === ALL_SPRINTS ? null : groups.find((group) => group.id === selectedId) || null;
  // a link to a sprint that no longer exists falls back to every sprint
  const isAll = !selectedGroup;

  const visibleGroups = useMemo(() => {
    const scope = selectedGroup ? [selectedGroup] : groups;
    if (!searchTerm) return scope;
    return scope
      .map((group) => {
        const matched = group.issues.filter((issue) => matchesSearch(issue, searchTerm));
        return { ...group, issues: matched };
      })
      .filter((group) => !isAll || group.issues.length);
  }, [groups, selectedGroup, searchTerm, isAll]);

  const visibleTasks = visibleGroups.reduce((sum, group) => sum + group.issues.length, 0);

  /* header figures follow the scope (one sprint or all of them), not the search */
  const scopeGroups = selectedGroup ? [selectedGroup] : groups;
  const totals = scopeGroups.reduce(
    (sum, group) => ({
      total: sum.total + group.total,
      done: sum.done + group.done,
      points: sum.points + group.points,
      donePoints: sum.donePoints + group.donePoints,
    }),
    { total: 0, done: 0, points: 0, donePoints: 0 }
  );
  const sprintGroups = groups.filter((group) => group.id !== BACKLOG);
  const runningSprint = groups.find((group) => group.status === "active");
  const progress = percent(totals.done, totals.total);

  const isCollapsed = (id) => Boolean(collapsedIds?.has(id)) && !searchTerm;
  const toggleSection = (id) =>
    setCollapsedIds((previous) => {
      const next = new Set(previous || []);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const allExpanded = groups.every((group) => !collapsedIds?.has(group.id));
  const toggleAll = () => setCollapsedIds(allExpanded ? new Set(groups.map((group) => group.id)) : new Set());

  /* The list endpoint leaves descriptions out, so they are fetched per sprint
     just for the download. A sprint that fails still exports, without them. */
  const descriptionsFor = async (sections) => {
    const lookups = await Promise.allSettled(
      sections
        .filter((group) => group.id !== BACKLOG && group.issues.length)
        .map((group) => axios.post(`${API_BASE_URL}/sprints/download`, { sprintname: group.name }))
    );
    const byId = {};
    lookups.forEach((result) => {
      const list = result.status === "fulfilled" ? result.value?.data?.issues : null;
      (Array.isArray(list) ? list : []).forEach((issue) => {
        if (issue?._id) byId[issue._id] = issue.description || "";
      });
    });
    return byId;
  };

  /* Exactly what is on screen: the chosen sprint (or every sprint) with the
     search applied. All sprints gives one combined sheet plus one per sprint. */
  const handleDownload = async () => {
    if (downloading) return;
    const sections = visibleGroups.filter((group) => group.issues.length);
    if (!sections.length) {
      toast.info("There are no tasks to download");
      return;
    }
    setDownloading(true);
    try {
      const descriptions = await descriptionsFor(sections);
      const workbook = XLSX.utils.book_new();
      const sheets = isAll
        ? [
            { name: "All tasks", rows: sections.flatMap((group) => group.issues.map((issue) => exportRowOf(issue, group.name, descriptions))) },
            ...sections.map((group) => ({ name: group.name, rows: group.issues.map((issue) => exportRowOf(issue, group.name, descriptions)) })),
          ]
        : [{ name: sections[0].name, rows: sections[0].issues.map((issue) => exportRowOf(issue, sections[0].name, descriptions)) }];

      const names = sheetNamesFor(sheets.map((sheet) => sheet.name));
      sheets.forEach((sheet, index) => {
        const worksheet = XLSX.utils.json_to_sheet(sheet.rows);
        worksheet["!cols"] = [{ wch: 18 }, { wch: 10 }, { wch: 42 }, { wch: 10 }, { wch: 50 }, { wch: 8 }, { wch: 10 }, { wch: 12 }, { wch: 22 }, { wch: 14 }, { wch: 12 }, { wch: 12 }];
        XLSX.utils.book_append_sheet(workbook, worksheet, names[index]);
      });

      const scopeName = isAll ? "All Sprints" : sections[0].name;
      XLSX.writeFile(workbook, `${fileNameFor(projectTitle, scopeName)}.xlsx`);
      toast.success(`${scopeName} downloaded`);
    } catch (error) {
      console.error(error);
      toast.error("Could not download the tasks");
    } finally {
      setDownloading(false);
    }
  };

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied");
    } catch (error) {
      toast.error("Could not copy the link");
    }
  };

  const openIssue = (row) => setOpenIssueId(row?._id || null);

  const pickerValue = selectedGroup ? selectedGroup.id : ALL_SPRINTS;
  const scopeLabel = selectedGroup ? selectedGroup.name : `${plural(sprintGroups.length, "sprint")} & backlog`;

  const renderPickerOption = (group) => (
    <span className="as-option">
      <span
        className={`as-option__dot ${group.status === "active" ? "is-live" : ""}`}
        style={{ background: sprintColorOf(group).color }}
      />
      <span className="as-option__name">{group.name}</span>
      <span className="as-option__meta">{STATUS_META[group.status].label}</span>
      <span className="as-option__count">{group.total}</span>
    </span>
  );

  const stats = selectedGroup
    ? [
        { key: "tasks", icon: <AssignmentRoundedIcon />, value: totals.total, label: "Tasks", detail: STATUS_META[selectedGroup.status].label },
        { key: "done", icon: <TaskAltRoundedIcon />, value: `${progress}%`, label: "Done", detail: `${totals.done} of ${totals.total}` },
        { key: "points", icon: <DonutSmallRoundedIcon />, value: totals.points, label: "Story points", detail: `${totals.donePoints} done` },
        {
          key: "dates",
          icon: <EventRoundedIcon />,
          value: timeLeftOf(selectedGroup) || (selectedGroup.id === BACKLOG ? "—" : STATUS_META[selectedGroup.status].label),
          label: "Timeline",
          detail: dateRangeOf(selectedGroup) || "No dates set",
        },
      ]
    : [
        {
          key: "sprints",
          icon: <DirectionsRunRoundedIcon />,
          value: sprintGroups.length,
          label: "Sprints",
          detail: runningSprint ? `${runningSprint.name} running` : "None running",
        },
        { key: "tasks", icon: <AssignmentRoundedIcon />, value: totals.total, label: "Tasks", detail: `${totals.total - totals.done} open` },
        { key: "done", icon: <TaskAltRoundedIcon />, value: `${progress}%`, label: "Done", detail: `${totals.done} of ${totals.total}` },
        { key: "points", icon: <DonutSmallRoundedIcon />, value: totals.points, label: "Story points", detail: `${totals.donePoints} done` },
      ];

  const showSkeleton = Skeleton || (loading && !issues.length);

  return (
    <div className="list-page-container-cls all-sprints-page">
      <section className="il-hero">
        <div className="il-hero__top">
          <div className="il-hero__crumbs">
            {!Skeleton ? <BreadcrumbsComponent /> : <NormalSkeleton width={200} extraStyles="my-1" />}
          </div>
          <div className="il-actions">
            <Tooltip title="Copy link to this view">
              <button type="button" className="il-icon-btn" onClick={handleCopyUrl} aria-label="Copy page link">
                <LinkRoundedIcon />
              </button>
            </Tooltip>
            <Button className="il-btn il-btn--ghost d-none d-sm-inline-flex" startIcon={<CampaignRoundedIcon />} disableRipple>
              Give feedback
            </Button>
          </div>
        </div>

        <div className="il-hero__main">
          {!Skeleton ? (
            <div className="il-hero__identity">
              <span className="il-hero__tile" aria-hidden="true">
                <ViewTimelineRoundedIcon />
              </span>
              <div className="il-hero__heading">
                <Typography component="h1" className="il-title">
                  {selectedGroup ? selectedGroup.name : "All sprints"}
                </Typography>
                <div className="il-hero__meta">
                  <span className="il-meta-chip">
                    <LayersRoundedIcon />
                    {scopeLabel}
                  </span>
                  {totals.total > 0 ? (
                    <Tooltip title={`${progress}% of these tasks are done`}>
                      <span className="il-progress">
                        <span className="il-progress__track">
                          <span className="il-progress__fill" style={{ width: `${progress}%` }} />
                        </span>
                        <span className="il-progress__label">
                          <TaskAltRoundedIcon />
                          {totals.done}/{totals.total} done
                        </span>
                      </span>
                    </Tooltip>
                  ) : null}
                </div>
              </div>
            </div>
          ) : (
            <NormalSkeleton width={240} extraStyles="my-1" />
          )}
          <Button className="il-btn il-btn--primary as-create" startIcon={<AddRoundedIcon />} onClick={() => setCreating(true)} disableRipple>
            Create task
          </Button>
        </div>

        {!showSkeleton ? (
          <div className="as-stats">
            {stats.map(({ key, ...stat }, index) => (
              <Stat key={key} {...stat} delay={index * 50} />
            ))}
          </div>
        ) : null}

        <div className="il-toolbar">
          <div className="il-toolbar__left">
            <Box sx={{ display: { xs: "none", md: "block" } }}>
              {!Skeleton ? (
                <SearchTextField searchTerm={searchTerm} handleSearch={setSearchTerm} />
              ) : (
                <NormalSkeleton variant="rectangular" width={200} height="30px" extraStyles="my-2" />
              )}
            </Box>
            {!Skeleton ? <GroupAvatars setOpenAddPeopleModel={setOpenAddPeopleModel} /> : <CircularSkeleton />}
          </div>

          <div className="il-toolbar__right as-toolbar-right">
            {!Skeleton ? (
              <>
                <Select
                  className="as-picker"
                  value={pickerValue}
                  onChange={(event) => selectSprint(event.target.value)}
                  inputProps={{ "aria-label": "Choose a sprint" }}
                  SelectDisplayProps={{ "aria-label": "Choose a sprint" }}
                  IconComponent={ExpandMoreRoundedIcon}
                  MenuProps={{ PaperProps: { className: "il-select-menu as-picker-menu", style: { maxHeight: 360 } } }}
                  renderValue={(value) => {
                    const group = groups.find((each) => each.id === value);
                    return (
                      <span className="as-picker__value">
                        <span className={`as-option__dot ${group?.status === "active" ? "is-live" : ""}`} />
                        {group ? group.name : "All Sprints"}
                      </span>
                    );
                  }}
                >
                  <MenuItem value={ALL_SPRINTS}>
                    <span className="as-option">
                      <span className="as-option__dot" />
                      <span className="as-option__name">All Sprints</span>
                      <span className="as-option__meta">Every sprint</span>
                      <span className="as-option__count">{issues.length}</span>
                    </span>
                  </MenuItem>
                  {groups.map((group) => (
                    <MenuItem key={group.id} value={group.id}>
                      {renderPickerOption(group)}
                    </MenuItem>
                  ))}
                </Select>
                <Tooltip title={visibleTasks ? `Download ${plural(visibleTasks, "task")} as Excel` : "Nothing to download"}>
                  <span className="as-download-wrap">
                    <Button
                      className="il-btn il-btn--primary"
                      type="button"
                      onClick={handleDownload}
                      disabled={downloading || !visibleTasks}
                      startIcon={<FileDownloadRoundedIcon />}
                      disableRipple
                    >
                      {downloading ? "Preparing…" : "Download"}
                    </Button>
                  </span>
                </Tooltip>
              </>
            ) : (
              <NormalSkeleton variant="rectangular" width={300} height="30px" extraStyles="my-2" />
            )}
          </div>
        </div>
      </section>

      {showSkeleton ? (
        <div className="py-4 px-3">
          <NormalSkeleton variant="rectangular" height={400} width="100%" />
        </div>
      ) : (
        <div className="as-sections">
          {isAll ? (
            <div className="as-sections__bar">
              <span>
                {searchTerm ? (
                  <>
                    <strong>{plural(visibleTasks, "task")}</strong> matching “{searchTerm}”
                  </>
                ) : (
                  <>
                    <strong>{plural(groups.length, "section")}</strong> · {plural(totals.total, "task")}
                  </>
                )}
              </span>
              {groups.length > 1 && !searchTerm ? (
                <button type="button" className="as-link-btn" onClick={toggleAll}>
                  {allExpanded ? <UnfoldLessRoundedIcon /> : <UnfoldMoreRoundedIcon />}
                  {allExpanded ? "Collapse all" : "Expand all"}
                </button>
              ) : null}
            </div>
          ) : (
            <div className="as-sections__bar">
              <button type="button" className="as-link-btn" onClick={() => selectSprint(ALL_SPRINTS)}>
                <ArrowBackRoundedIcon />
                All sprints
              </button>
              {searchTerm ? (
                <span>
                  <strong>{plural(visibleTasks, "task")}</strong> matching “{searchTerm}”
                </span>
              ) : null}
            </div>
          )}

          {visibleGroups.map((group, index) => (
            <SprintSection
              key={group.id}
              index={index}
              group={group}
              collapsible={isAll}
              collapsed={isAll && isCollapsed(group.id)}
              onToggle={() => toggleSection(group.id)}
              onFocus={isAll ? () => selectSprint(group.id) : null}
              onOpenIssue={openIssue}
              searching={Boolean(searchTerm)}
              pageSize={isAll ? 10 : 25}
            />
          ))}

          {!visibleGroups.length ? (
            <div className="as-empty">
              <span className="il-empty__icon">
                <SearchOffRoundedIcon />
              </span>
              <strong>{searchTerm ? "No tasks match" : "No sprints or tasks yet"}</strong>
              <span>{searchTerm ? "Try another word, or clear the search." : "Create a task, or start a sprint from the backlog."}</span>
            </div>
          ) : null}
        </div>
      )}

      {creating && <CreateBacklogIssue isCreateNewBackIssuse={creating} setIsCreateNewBackIssue={setCreating} />}

      {openIssueId && (
        <TaskWorkFlowModalPage
          open={Boolean(openIssueId)}
          activeSprintName={activeSprint?.name}
          handleChangeModalOpen={() => setOpenIssueId(null)}
          selectedissueId={openIssueId}
        />
      )}

      {openAddPeopleModel && (
        <AddPeopleToProject open={openAddPeopleModel} ProjectName={ProjectName} setOpenAddPeopleModel={setOpenAddPeopleModel} />
      )}
    </div>
  );
};

export default IssuesList;
